import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, GitBranch } from 'lucide-react';
import { NODE_TYPES } from '../WorkflowBuilder/nodeTypes';

/**
 * WorkflowPreviewCard — Inline chat component that visualizes the workflow being built.
 * Shows nodes as small cards with icons/labels and arrows between them.
 * Compact vertical layout that fits within chat width.
 *
 * ABLE-29: WorkflowPreviewCard chat component
 */
export default function WorkflowPreviewCard({ workflowState }) {
  const { workflow_id, name, nodes = [], edges = [] } = workflowState;

  if (nodes.length === 0) return null;

  // Build adjacency for rendering order: start from trigger, follow edges
  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });

  const childrenOf = {};
  edges.forEach(e => {
    if (!childrenOf[e.source]) childrenOf[e.source] = [];
    childrenOf[e.source].push(e.target);
  });

  // Find root nodes (no incoming edges)
  const hasIncoming = new Set(edges.map(e => e.target));
  const roots = nodes.filter(n => !hasIncoming.has(n.id));
  if (roots.length === 0 && nodes.length > 0) roots.push(nodes[0]);

  // BFS to get ordered list
  const ordered = [];
  const visited = new Set();
  const queue = [...roots];
  while (queue.length > 0) {
    const node = queue.shift();
    if (visited.has(node.id)) continue;
    visited.add(node.id);
    ordered.push(node);
    (childrenOf[node.id] || []).forEach(childId => {
      if (nodeMap[childId] && !visited.has(childId)) {
        queue.push(nodeMap[childId]);
      }
    });
  }
  // Add any unvisited nodes at the end
  nodes.forEach(n => {
    if (!visited.has(n.id)) ordered.push(n);
  });

  return (
    <div className="my-2 rounded-xl border border-gray-200 dark:border-gray-700/80 bg-white dark:bg-gray-800/60 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/80 dark:bg-gray-800/40">
        <GitBranch className="w-3.5 h-3.5 text-brand-500 dark:text-brand-400" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
          {name || 'New Workflow'}
        </span>
        <span className="text-[0.6875rem] text-gray-400 dark:text-gray-500 ml-auto">
          {ordered.length} node{ordered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Node list */}
      <div className="px-3 py-2.5 space-y-0">
        {ordered.map((node, idx) => {
          const typeDef = NODE_TYPES[node.type] || {};
          const Icon = typeDef.icon || GitBranch;
          const color = typeDef.color || '#64748b';
          const isLast = idx === ordered.length - 1;
          const isTrigger = typeDef.category === 'trigger';

          return (
            <div key={node.id} className="flex items-stretch gap-0">
              {/* Vertical connector */}
              <div className="flex flex-col items-center w-6 flex-shrink-0">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ring-2 ring-white dark:ring-gray-800"
                  style={{ backgroundColor: color + '18', borderColor: color }}
                >
                  <Icon className="w-2.5 h-2.5" style={{ color }} />
                </div>
                {!isLast && (
                  <div className="w-px flex-1 min-h-[16px] bg-gray-200 dark:bg-gray-600/60" />
                )}
              </div>

              {/* Node label */}
              <div className="flex items-start gap-1.5 pb-2 pl-1.5 min-w-0">
                <div className="pt-0.5">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200 leading-tight truncate">
                    {node.label}
                  </p>
                  <p className="text-[0.625rem] text-gray-400 dark:text-gray-500 leading-tight">
                    {isTrigger ? 'Trigger' : typeDef.label || node.type}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer: Open in Builder link */}
      {workflow_id && (
        <div className="px-3 py-1.5 border-t border-gray-100 dark:border-gray-700/60">
          <Link
            to={`/workflows/${workflow_id}/builder`}
            className="inline-flex items-center gap-1 text-[0.6875rem] text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            Open in Builder
          </Link>
        </div>
      )}
    </div>
  );
}
